import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useNavigate } from 'react-router-dom';
import Seo from '@/seo/Seo';

const services = [
  {
    icon: 'ri-truck-line',
    color: 'bg-orange-500',
    title: 'Parsiyel Taşımacılık',
    desc: 'Küçük ve orta ölçekli yüklerinizi diğer kargolarla birleştirerek ekonomik ve hızlı şekilde yurtiçi ve yurtdışı hedeflere ulaştırıyoruz. Parsiyel taşımacılık ile hem maliyetlerinizi düşürür hem de yükünüzün güvenle teslim edilmesini sağlarsınız.',
    details: ['Esnek yükleme seçenekleri', 'Rekabetçi fiyatlandırma', 'Haftalık düzenli seferler', 'Takip sistemi'],
  },
  {
    icon: 'ri-box-3-line',
    color: 'bg-slate-800',
    title: 'Komple Araç Kiralama',
    desc: 'Büyük hacimli yükleriniz için komple araç kiralama hizmeti sunuyor, yükünüzü doğrudan hedef noktaya teslim ediyoruz. Tek müşteriye özel araç tahsisi ile maksimum güvenlik sağlanır.',
    details: ['Özel araç tahsisi', 'Doğrudan teslimat', 'Esnek güzergah', 'Öncelikli hizmet'],
  },
  {
    icon: 'ri-flashlight-line',
    color: 'bg-amber-500',
    title: 'Ekspres Teslimat',
    desc: 'Acil yükleriniz için öncelikli ekspres taşımacılık hizmeti ile en kısa sürede teslimat garantisi veriyoruz. Zaman kritik gönderileriniz için özel çözümler sunuyoruz.',
    details: ['48-72 saat teslimat', 'Öncelikli gümrük', '7/24 takip', 'Acil müdahale ekibi'],
  },
  {
    icon: 'ri-file-list-3-line',
    color: 'bg-emerald-600',
    title: 'Gümrük İşlemleri',
    desc: 'Deneyimli ekibimizle tüm gümrük prosedürlerini sizin adınıza yönetiyor, bürokratik süreçleri kolaylaştırıyoruz. Yurtiçi ve uluslararası gümrük mevzuatına tam uyum sağlıyoruz.',
    details: ['Evrak hazırlama', 'Gümrük beyannamesi', 'Vergi danışmanlığı', 'Mevzuat takibi'],
  },
  {
    icon: 'ri-store-2-line',
    color: 'bg-rose-600',
    title: 'Depolama Hizmetleri',
    desc: 'Yurtiçi ve yurtdışı iş ortaklarımız aracılığıyla geçici depolama çözümleri sunuyoruz. Yükleriniz güvenli depolarda bekletilir ve zamanında teslim edilir.',
    details: ['Güvenli depolama', 'Sigortalı stok', 'Esnek süre', 'Envanter takibi'],
  },
  {
    icon: 'ri-map-pin-line',
    color: 'bg-indigo-600',
    title: 'Kapıdan Kapıya Teslimat',
    desc: 'Yükünüzü Türkiye\'deki adresinizden alıp hedef ülkedeki adrese kadar eksiksiz teslim ediyoruz. Tüm süreç tek elden yönetilir.',
    details: ['Adres bazlı teslim', 'Tek elden yönetim', 'Teslimat onayı', 'İmzalı teslim belgesi'],
  },
];

export default function ServicesPage() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen" style={{ fontFamily: "'Inter', sans-serif" }}>
      <Seo
        title="Hizmetlerimiz | Global79 Lojistik"
        description="Parsiyel taşımacılık, komple araç kiralama, ekspres teslimat, gümrük işlemleri ve kapıdan kapıya teslimat hizmetleriyle Global79 Lojistik tüm lojistik ihtiyaçlarınıza çözüm sunar."
        path="/hizmetler"
        keywords="parsiyel taşımacılık, komple araç kiralama, ekspres teslimat, gümrük işlemleri, kapıdan kapıya teslimat"
        ogImage="/assets/images/hero-slide-2.png"
      />
      <Navbar />

      {/* Page Hero */}
      <section className="relative h-72 md:h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=logistics%20trucks%20lined%20up%20in%20modern%20transport%20depot%2C%20professional%20fleet%20management%2C%20industrial%20setting%2C%20dramatic%20lighting%2C%20high%20quality%20commercial%20photography%2C%20ultra%20realistic&width=1920&height=600&seq=srvhero01&orientation=landscape"
          alt="Hizmetler hero"
          className="absolute inset-0 w-full h-full object-cover object-center"
          fetchPriority="high"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70" />
        <div className="relative z-10 text-center px-4">
          <span className="text-orange-400 text-sm font-bold uppercase tracking-widest mb-3 block">Ne Sunuyoruz</span>
          <h1 className="text-4xl md:text-6xl font-extrabold text-white" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Hizmetlerimiz
          </h1>
          <p className="text-gray-300 mt-4 text-base max-w-xl mx-auto">
            Yurtiçi, yurtdışı ve uluslararası taşımacılıkta ihtiyacınıza özel kapsamlı lojistik çözümler
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-16">
            <span className="text-orange-500 text-sm font-bold uppercase tracking-widest mb-3 block">Hizmetlerimiz</span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Sunduğumuz <span className="text-orange-500">Lojistik Çözümler</span>
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              Müşterilerimizin ihtiyaçlarına özel, esnek ve güvenilir taşımacılık çözümleri sunuyoruz.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((s) => (
              <div key={s.title} className="rounded-2xl overflow-hidden border border-gray-100 hover:-translate-y-2 transition-transform group cursor-pointer">
                <div className={`${s.color} h-32 flex items-center justify-center`}>
                  <div className="w-16 h-16 flex items-center justify-center bg-white/20 rounded-2xl">
                    <i className={`${s.icon} text-white text-3xl`} />
                  </div>
                </div>
                <div className="p-7">
                  <h3 className="font-bold text-gray-900 text-xl mb-3">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed mb-5">{s.desc}</p>
                  <ul className="flex flex-col gap-2">
                    {s.details.map((d) => (
                      <li key={d} className="flex items-center gap-2 text-sm text-gray-600">
                        <i className="ri-check-line text-orange-500 text-base" />
                        {d}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-20" style={{ background: '#f8f6f3' }}>
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Taşımacılık İhtiyacınız mı Var?
          </h2>
          <p className="text-gray-500 text-base mb-8 max-w-xl mx-auto">
            Size en uygun lojistik çözümü bulmak için hemen iletişime geçin.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:05382323822"
              className="flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-8 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill" />
              0 538 232 38 22
            </a>
            <button
              onClick={() => navigate('/iletisim')}
              className="flex items-center justify-center gap-2 border-2 border-gray-300 hover:border-orange-500 text-gray-700 hover:text-orange-500 font-bold px-8 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-right-line" />
              İletişim Sayfası
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}